/**
 * SKU列表页面
 * 职责: SKU列表展示和管理
 * 限制: <280行
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Drawer, Space, Card, App as AntApp, Form } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { SKU } from '../types';
import { skuAPI } from '../services/api';
import { extractResultsFromResponse } from '../utils/apiResponse';
import { createSKUColumns } from './SKUList/columns';
import { SKUForm } from './SKUList/SKUForm';

export const SKUList: React.FC = () => {
  const [skus, setSkus] = useState<SKU[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSKU, setEditingSKU] = useState<SKU | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { modal, message } = AntApp.useApp();

  const loadSKUs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await skuAPI.list();
      const skuList = extractResultsFromResponse<SKU>(response.data);
      setSkus(skuList);
    } catch (error: any) {
      console.error('加载SKU列表失败:', error);
      const errorMsg = error.response?.data?.detail ||
        error.userMessage ||
        error.message ||
        '加载SKU列表失败，请检查后端服务是否正常运行';
      message.error(errorMsg);
      setSkus([]);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadSKUs();
  }, [loadSKUs]);

  const handleCreate = () => {
    setEditingSKU(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (sku: SKU) => {
    setEditingSKU(sku);
    form.setFieldsValue(sku);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    modal.confirm({
      title: '确认删除',
      content: '删除后将无法恢复，确定要删除这个SKU吗？',
      onOk: async () => {
        try {
          await skuAPI.delete(id);
          message.success('删除成功');
          loadSKUs();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingSKU) {
        await skuAPI.update(editingSKU.id, values);
        message.success('更新成功');
      } else {
        await skuAPI.create(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadSKUs();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = createSKUColumns(
    (id) => navigate(`/skus/${id}`),
    handleEdit,
    handleDelete
  );

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <h2>SKU管理</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            新建SKU
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={Array.isArray(skus) ? skus : []}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Drawer
        title={editingSKU ? '编辑SKU' : '新建SKU'}
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        width={720}
        placement="right"
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setModalVisible(false)}>
              取消
            </Button>
            <Button type="primary" onClick={() => form.submit()}>
              {editingSKU ? '保存' : '创建'}
            </Button>
          </Space>
        }
      >
        <SKUForm form={form} onFinish={handleSubmit} />
      </Drawer>
    </div>
  );
};

export default SKUList;
